import React, { useEffect, useState } from "react";
import { Mail, MessageSquare, Bell, CalendarClock, FileBarChart, Activity } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CHANNELS = [
  { key: "email", icon: Mail, label: "Email notifications", desc: "Renewal reminders, filing confirmations & updates." },
  { key: "sms", icon: MessageSquare, label: "SMS alerts", desc: "Text me urgent deadlines.", note: "Activates once SMS (Twilio) is connected." },
  { key: "push", icon: Bell, label: "Push notifications", desc: "Instant alerts in your browser." },
  { key: "weekly_report", icon: FileBarChart, label: "Weekly compliance report", desc: "A Monday recap of everything Dox handled." },
  { key: "monthly_report", icon: Activity, label: "Monthly business health report", desc: "Your compliance trend & time saved." },
];

export default function Settings() {
  const [prefs, setPrefs] = useState(null);
  const [report, setReport] = useState(null);

  useEffect(() => { api.get("/settings").then(({ data }) => setPrefs(data)).catch(() => {}); }, []);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try { await api.put("/settings", next); toast.success("Preferences saved."); }
    catch { toast.error("Could not save."); }
  };

  const viewReport = async (period) => {
    try { const { data } = await api.get(`/reports/${period}`); setReport(data); } catch { toast.error("Could not build report."); }
  };

  if (!prefs) return <div className="h-40 rounded-2xl bg-white/5 animate-pulse" />;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-black text-2xl mb-1">Notifications & Reports</h2>
        <p className="text-white/55 text-sm">Choose how Dox keeps you in the loop.</p>
      </div>

      <div className="glass-card divide-y divide-white/5">
        {CHANNELS.map((c) => (
          <div key={c.key} className="flex items-center gap-4 p-4">
            <span className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-dox-orange shrink-0"><c.icon size={18} /></span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-white">{c.label}</div>
              <div className="text-xs text-white/45">{c.desc}{c.note && <span className="text-amber-400/80"> · {c.note}</span>}</div>
            </div>
            <button
              data-testid={`toggle-${c.key}`}
              onClick={() => toggle(c.key)}
              className={`relative h-6 w-11 rounded-full transition-colors shrink-0 ${prefs[c.key] ? "bg-dox-orange" : "bg-white/15"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${prefs[c.key] ? "left-[22px]" : "left-0.5"}`} />
            </button>
          </div>
        ))}
      </div>

      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><CalendarClock size={18} className="text-dox-magenta" /> Generate a report</h3>
          <div className="flex gap-2">
            <button data-testid="report-weekly" onClick={() => viewReport("weekly")} className="rounded-full glass px-4 py-2 text-sm font-semibold hover:bg-white/10">Weekly</button>
            <button data-testid="report-monthly" onClick={() => viewReport("monthly")} className="rounded-full glass px-4 py-2 text-sm font-semibold hover:bg-white/10">Monthly</button>
          </div>
        </div>
        {report ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="report-result">
            <Metric label="Documents" value={report.documents_managed} />
            <Metric label="Compliance" value={`${report.compliance_score}%`} />
            <Metric label="Hours saved" value={`${report.hours_saved}h`} />
            <Metric label="Money saved" value={`$${report.money_saved}`} />
          </div>
        ) : <p className="text-white/40 text-sm">Pick a period to preview your report. Email delivery activates once Resend is connected.</p>}
      </div>

      <GoogleCalendarPanel />
      <EmailAddressPanel />
    </div>
  );
}

function EmailAddressPanel() {
  const [me, setMe] = useState(null);
  const [suppressed, setSuppressed] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/me");
      const email = data?.email || data?.user?.email;
      setMe({ email });
      if (email) {
        const supp = await api.get(`/emails/suppressions/check?email=${encodeURIComponent(email)}`);
        setSuppressed(!!supp.data?.suppressed);
      }
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const swap = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-email", { new_email: newEmail, password });
      toast.success("Email updated. I&apos;ll start sending here immediately.");
      setNewEmail(""); setPassword("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update your email.");
    } finally {
      setBusy(false);
    }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/dox/digest/send-me-now", {});
      const st = data?.result?.status;
      if (st === "success") toast.success("Weekly digest sent — check your inbox.");
      else if (st === "suppressed") toast.error("Your email is on the suppression list. Update it below to receive emails again.");
      else toast.message("Digest attempt logged.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the digest.");
    } finally {
      setSendingDigest(false);
    }
  };

  if (!me) return null;

  return (
    <div className="glass-card p-6" data-testid="email-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
            <Mail size={20} className="text-dox-orange" />
          </div>
          <div>
            <div className="font-semibold text-white">Email address</div>
            <div className="text-xs text-white/50">Where Dox reaches you for reminders, digests, and reset links.</div>
          </div>
        </div>
        <button
          onClick={sendDigest}
          disabled={sendingDigest}
          className="rounded-full glass-strong border border-white/10 px-4 py-2 min-h-[44px] text-sm font-semibold hover:bg-white/10 disabled:opacity-60"
          data-testid="send-weekly-digest-btn"
        >
          {sendingDigest ? "Sending…" : "Send weekly digest now"}
        </button>
      </div>

      <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-3 flex items-center justify-between gap-3 mb-3">
        <div className="text-sm text-white/85 truncate">
          Current: <span className="font-semibold text-white">{me.email}</span>
        </div>
        {suppressed ? (
          <span className="rounded-full bg-rose-500/15 border border-rose-400/40 px-2.5 py-1 text-[11px] font-bold text-rose-300" data-testid="email-suppressed-tag">Blocked — bouncing</span>
        ) : (
          <span className="rounded-full bg-emerald-500/15 border border-emerald-400/40 px-2.5 py-1 text-[11px] font-bold text-emerald-300">Delivering</span>
        )}
      </div>

      {suppressed && (
        <div className="rounded-xl border border-rose-400/30 bg-rose-500/[0.08] px-4 py-3 mb-3 text-sm text-rose-100">
          Your address bounced. Update it below and I&apos;ll unblock delivery automatically.
        </div>
      )}

      <form onSubmit={swap} className="grid sm:grid-cols-2 gap-3 items-end" data-testid="change-email-form">
        <label className="block">
          <span className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">New email</span>
          <input
            data-testid="new-email-input"
            required
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="you@company.com"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 min-h-[48px] text-white outline-none focus:border-dox-orange focus:ring-2 focus:ring-dox-orange/20 transition"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5 block">Current password</span>
          <div className="flex gap-2">
            <input
              data-testid="current-password-input"
              required
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 min-h-[48px] text-white outline-none focus:border-dox-orange focus:ring-2 focus:ring-dox-orange/20 transition"
            />
            <button
              disabled={busy || !newEmail || !password}
              className="rounded-full bg-dox-orange hover:bg-dox-orangeHover text-white font-bold px-5 py-2 min-h-[48px] text-sm disabled:opacity-40"
              data-testid="change-email-submit"
            >
              {busy ? "Updating…" : "Update"}
            </button>
          </div>
        </label>
      </form>
    </div>
  );
}

function GoogleCalendarPanel() {
  const [status, setStatus] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(async () => {
    try { const { data } = await api.get("/oauth/calendar/status"); setStatus(data); } catch { /* ignore */ }
    try { const { data } = await api.get("/calendar/coverage"); setCoverage(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    // If we just came back from OAuth, refresh
    const params = new URLSearchParams(window.location.search);
    if (params.get("gcal") === "connected") { toast.success("Google Calendar connected."); load(); }
    if (params.get("gcal") === "error") { toast.error("Google Calendar connection failed."); }
  }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/oauth/calendar/login");
      window.location.href = data.authorization_url;
    } catch { setBusy(false); toast.error("Could not start Google connect."); }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await api.post("/oauth/calendar/disconnect", {}); toast.success("Disconnected."); load(); }
    catch { toast.error("Could not disconnect."); }
    finally { setBusy(false); }
  };

  const syncAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/oauth/calendar/sync-all", {});
      toast.success(`Dox pushed ${data.pushed} deadline${data.pushed === 1 ? "" : "s"} to your Google Calendar.`);
      load();
    } catch { toast.error("Sync failed."); }
    finally { setBusy(false); }
  };

  if (!status) return <div className="h-40 rounded-2xl bg-white/5 animate-pulse" />;

  return (
    <div className="glass-card p-6" data-testid="gcal-panel">
      {status.needs_reconnect && (
        <div className="mb-4 rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-3 text-amber-200 text-sm flex items-start gap-2" data-testid="gcal-needs-reconnect">
          <span className="mt-0.5">⚠️</span>
          <div>
            <div className="font-semibold text-amber-100">Your Google Calendar access expired.</div>
            <div className="text-amber-200/85 text-xs mt-0.5">
              Google revoked our token, so I stopped writing deadlines. Reconnect below and I&apos;ll catch up
              every renewal on your Calendar again.
            </div>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
            <CalendarClock size={20} className="text-dox-orange" />
          </div>
          <div>
            <div className="font-semibold text-white">Google Calendar</div>
            <div className="text-xs text-white/50">
              {status.connected
                ? <>Connected as <span className="text-white/80">{status.google_email}</span> — Dox writes deadlines here automatically.</>
                : status.needs_reconnect
                  ? "Reconnect to resume automatic deadline sync."
                  : "Let Dox write every deadline directly to your calendar."}
            </div>
            {status.connected && coverage && coverage.total > 0 && (
              <div className="mt-3 max-w-md" data-testid="gcal-coverage">
                <div className="flex items-center justify-between text-[11px] text-white/60 mb-1">
                  <span>
                    <span className="font-bold text-white">{coverage.synced}</span>
                    <span className="text-white/50"> of </span>
                    <span className="font-bold text-white">{coverage.total}</span>
                    <span className="text-white/50"> deadlines on your calendar</span>
                  </span>
                  <span className={`font-bold ${coverage.percent === 100 ? "text-emerald-300" : coverage.percent >= 70 ? "text-dox-lightBlue" : "text-amber-300"}`}>{coverage.percent}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                  <div
                    className={`h-full transition-all ${coverage.percent === 100 ? "bg-emerald-400" : "bg-dox-blue"}`}
                    style={{ width: `${coverage.percent}%` }}
                  />
                </div>
                {coverage.percent < 100 && (
                  <div className="text-[10px] text-white/45 mt-1">Hit &ldquo;Sync all deadlines&rdquo; to push the remaining {coverage.total - coverage.synced}.</div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status.connected ? (
            <>
              <button data-testid="gcal-sync-btn" onClick={syncAll} disabled={busy} className="rounded-full glass-strong border border-white/10 px-4 py-2 min-h-[44px] text-sm font-semibold hover:bg-white/10 transition-colors disabled:opacity-60">
                Sync all deadlines
              </button>
              <button data-testid="gcal-disconnect-btn" onClick={disconnect} disabled={busy} className="rounded-full border border-white/10 px-4 py-2 min-h-[44px] text-sm text-white/70 hover:text-white hover:border-white/25 transition-colors disabled:opacity-60">
                Disconnect
              </button>
            </>
          ) : (
            <button data-testid="gcal-connect-btn" onClick={connect} disabled={busy || !status.configured} className="rounded-full bg-dox-orange px-5 py-2.5 min-h-[44px] text-sm font-bold hover:bg-dox-orangeHover transition-colors disabled:opacity-60">
              {status.configured ? (status.needs_reconnect ? "Reconnect Google Calendar" : "Connect Google Calendar") : "Not configured"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
      <div className="font-display font-black text-2xl">{value}</div>
      <div className="text-xs text-white/45 uppercase tracking-wider">{label}</div>
    </div>
  );
}
